import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle2, MinusCircle } from 'lucide-react';
import type { CustomerImportResult, ImportRowResult } from '@tailoring-crm/shared';
import { Badge } from '@tailoring-crm/ui';
import { formatMobile } from '../../lib/format.js';

/**
 * What the file will become, row by row, before a single customer is written.
 *
 * The rows that will FAIL are listed first and are never collapsed away. A preview that leads
 * with two hundred green ticks and hides fourteen problems below the fold is a preview that
 * gets scrolled past — and the fourteen are the only rows the owner can still do anything
 * about.
 */

const MAX_ROWS_SHOWN = 50;

function statusIcon(status: ImportRowResult['status']): ReactElement {
  if (status === 'failed')
    return <AlertCircle aria-hidden="true" className="size-4 text-destructive" />;
  if (status === 'skipped')
    return <MinusCircle aria-hidden="true" className="size-4 text-muted-foreground" />;
  return <CheckCircle2 aria-hidden="true" className="size-4 text-primary" />;
}

export function ImportPreview({ result }: { result: CustomerImportResult }): ReactElement {
  const { t } = useTranslation();
  const { summary } = result;

  // Problems first, then everything else in file order.
  const order = { failed: 0, skipped: 1, ready: 2, created: 3 } as const;
  const rows = [...result.rows]
    .sort((a, b) => order[a.status] - order[b.status] || a.rowNumber - b.rowNumber)
    .slice(0, MAX_ROWS_SHOWN);

  return (
    <div className="flex flex-col gap-4">
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4" role="status">
        {(
          [
            ['total', summary.total],
            ['ready', summary.created > 0 ? summary.created : summary.ready],
            ['skipped', summary.skipped],
            ['failed', summary.failed],
          ] as const
        ).map(([key, value]) => (
          <div key={key} className="rounded-lg border border-border p-3">
            <dt className="text-xs text-muted-foreground">
              {t(`import.summary.${summary.created > 0 && key === 'ready' ? 'created' : key}`)}
            </dt>
            <dd className="text-lg font-semibold tabular-nums">{value}</dd>
          </div>
        ))}
      </dl>

      {summary.profilesCreated > 0 ? (
        <p className="text-sm text-muted-foreground">
          {t('import.summary.profiles', { count: summary.profilesCreated })}
        </p>
      ) : null}

      <ul className="flex flex-col gap-2">
        {rows.map((row) => (
          <li
            key={row.rowNumber}
            className="flex flex-wrap items-start gap-3 rounded-lg border border-border p-3"
          >
            <span className="mt-0.5">{statusIcon(row.status)}</span>
            <div className="min-w-0 flex-1">
              <p className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{row.name ?? t('import.row.unreadable')}</span>
                {row.phone ? (
                  <span className="text-sm text-muted-foreground">{formatMobile(row.phone)}</span>
                ) : null}
                <Badge variant="secondary">
                  {t('import.row.number', { number: row.rowNumber })}
                </Badge>
                {row.measurementCount > 0 ? (
                  <Badge variant="secondary">
                    {t('import.row.measurements', { count: row.measurementCount })}
                  </Badge>
                ) : null}
              </p>
              {row.issues.length > 0 ? (
                <ul className="mt-1 flex flex-col gap-0.5">
                  {row.issues.map((issue, index) => (
                    <li
                      key={`${issue.code}-${String(index)}`}
                      className={
                        issue.severity === 'error'
                          ? 'text-sm text-destructive'
                          : 'text-sm text-muted-foreground'
                      }
                    >
                      {issue.message}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </li>
        ))}
      </ul>

      {result.rows.length > MAX_ROWS_SHOWN ? (
        <p className="text-sm text-muted-foreground">
          {t('import.moreRows', { count: result.rows.length - MAX_ROWS_SHOWN })}
        </p>
      ) : null}
    </div>
  );
}
